import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Search, RefreshCw, Eye, Package, Activity } from 'lucide-react';
import SiparisDetayModal from '../production/SiparisDetayModal';
import { BaskiGorselComponent } from '../production/BaskiGorselLazyImage';
import { customerPortalService } from '../../services/customerPortalApi';
import toast from 'react-hot-toast';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import config from '../../config/config';

/**
 * DigitalPrintingAktifSiparislerTab - Müşteri Portalı Aktif Siparişler Tabı
 * Sadece o müşterinin aktif ve kısmi sevk durumundaki siparişlerini gösterir
 * Sadece görüntüleme modu - düzenleme, silme, kritik ekleme, onaylama butonları YOK
 */
const DigitalPrintingAktifSiparislerTab = () => {
  const { customer } = useCustomerAuth();
  const [siparisler, setSiparisler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [durumFilter, setDurumFilter] = useState('');
  const [onayDurumuFilter, setOnayDurumuFilter] = useState(''); // Varsayılan: Tümü
  const [stats, setStats] = useState({
    toplam: 0,
    aktif: 0,
    kismiSevk: 0,
    toplamMetre: 0,
    aktifMetre: 0,
    kismiSevkMetre: 0
  });
  
  // Sipariş detay modalı state'leri
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSiparis, setSelectedSiparis] = useState(null);

  // Siparişleri yükle
  const loadSiparisler = useCallback(async () => {
    if (!customer?.id) {
      return;
    }

    setLoading(true);
    try {
      const response = await customerPortalService.get('/digital-printing/aktif-siparisler');
      if (response.data.success) {
        const filteredData = response.data.data || [];
        
        // Varyantları düzleştir
        const variants = filteredData.flatMap(siparis => 
          (siparis.varyantlar || [])
            .filter(varyant => varyant.durum === 'Aktif' || varyant.durum === 'Kısmi Sevk')
            .map(varyant => ({ siparis, varyant }))
        );
        
        setSiparisler(variants);
        
        // İstatistikleri hesapla
        const toplam = variants.length;
        const aktif = variants.filter(x => x.varyant.durum === 'Aktif').length;
        const kismiSevk = variants.filter(x => x.varyant.durum === 'Kısmi Sevk').length;
        
        const toplamMetre = variants.reduce((sum, x) => sum + parseFloat(x.varyant.metre || 0), 0);
        const aktifMetre = variants.filter(x => x.varyant.durum === 'Aktif').reduce((sum, x) => sum + parseFloat(x.varyant.metre || 0), 0);
        const kismiSevkMetre = variants.filter(x => x.varyant.durum === 'Kısmi Sevk').reduce((sum, x) => sum + parseFloat(x.varyant.metre || 0), 0);
        
        setStats({ toplam, aktif, kismiSevk, toplamMetre, aktifMetre, kismiSevkMetre });
      }
    } catch (error) {
      toast.error('Siparişler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [customer?.id]);

  // Component mount olduğunda verileri yükle
  useEffect(() => {
    loadSiparisler();
  }, [loadSiparisler]);

  // Sipariş detayını göster
  const handleShowDetail = (item) => {
    const { siparis, varyant } = item;
    
    const siparisData = {
      ...siparis,
      varyantlar: siparis.varyantlar || [varyant]
    };
    
    setSelectedSiparis(siparisData);
    setShowDetailModal(true);
  };

  // Durum değiştir
  const handleDurumChange = (yeniDurum) => {
    setDurumFilter(yeniDurum);
  };

  // Arama değiştir
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filtrelenmiş siparişler
  const filteredSiparisler = siparisler.filter(item => {
    // Durum filtresi
    if (durumFilter && item.varyant.durum !== durumFilter) return false;
    
    // Arama filtresi
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const siparisNo = (item.siparis.siparis_no || '').toLowerCase();
      const desenNo = (item.varyant.desen?.desen_no || '').toLowerCase();
      const musteriAdi = (item.siparis.musteri_adi || '').toLowerCase();
      
      if (!siparisNo.includes(searchLower) && 
          !desenNo.includes(searchLower) && 
          !musteriAdi.includes(searchLower)) {
        return false;
      }
    }
    
    // Desen onay durumu filtresi
    if (onayDurumuFilter) {
      const desenOnayDurumu = item.varyant.desen?.onay_durumu || null;
      if (onayDurumuFilter === 'onayli' && desenOnayDurumu !== 'onayli') return false;
      if (onayDurumuFilter === 'revize' && desenOnayDurumu !== 'revize') return false;
      if (onayDurumuFilter === 'onay_bekliyor' && desenOnayDurumu !== 'onay_bekliyor') return false;
    }
    
    return true;
  });

  // Görsel URL'lerini işle - Modal'daki processImageUrl mantığıyla aynı
  const processImageUrl = (gorselUrls) => {
    if (!gorselUrls) return null;
    
    let urls = gorselUrls;
    if (typeof urls === 'string') {
      try {
        urls = JSON.parse(urls);
      } catch (e) {
        urls = [urls];
      }
    }
    
    if (Array.isArray(urls) && urls.length > 0) {
      const first = urls[0];
      const cleanUrl = String(first).replace(/^["']+|["']+$/g, '');
      return cleanUrl.startsWith('http') ? cleanUrl : `${config.backendUrl}${cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`}`;
    }
    
    return null;
  };

  return (
    <div className="space-y-4 md:space-y-6 -mx-3 sm:-mx-4 lg:-mx-6 px-3 sm:px-4 lg:px-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
        <div>
          <h2 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">Aktif Siparişler</h2>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">Aktif ve kısmi sevk durumundaki siparişleriniz</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button 
            onClick={loadSiparisler}
            className="inline-flex items-center justify-center px-4 py-2.5 sm:py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 w-full sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 sm:h-5 sm:w-5 ${loading ? 'animate-spin' : ''}`} />
            <span className="ml-2">Yenile</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3 lg:gap-4">
        {/* Toplam */}
        <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/20 dark:to-slate-800/20 border border-slate-200 dark:border-slate-700 p-2.5 sm:p-3 rounded-lg sm:rounded-xl shadow-sm hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-1.5">Toplam</p>
              <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">{stats.toplam}</p>
              <p className="text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400 mt-1">{stats.toplamMetre.toFixed(1)} m</p>
            </div>
            <div className="bg-slate-100 dark:bg-slate-800/30 p-2 sm:p-3 rounded-lg">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-slate-600 dark:text-slate-400" />
            </div>
          </div>
        </div>

        {/* Aktif */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border border-blue-200 dark:border-blue-700 p-2.5 sm:p-3 rounded-lg sm:rounded-xl shadow-sm hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1.5">Aktif</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-900 dark:text-blue-100">{stats.aktif}</p>
              <p className="text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-400 mt-1">{stats.aktifMetre.toFixed(1)} m</p>
            </div>
            <div className="bg-blue-100 dark:bg-blue-800/30 p-2 sm:p-3 rounded-lg">
              <Activity className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        {/* Kısmi Sevk */}
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 border border-yellow-200 dark:border-yellow-700 p-2.5 sm:p-3 rounded-lg sm:rounded-xl shadow-sm hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-yellow-600 dark:text-yellow-400 uppercase tracking-wide mb-1.5">Kısmi Sevk</p>
              <p className="text-xl sm:text-2xl font-bold text-yellow-900 dark:text-yellow-100">{stats.kismiSevk}</p>
              <p className="text-xs sm:text-sm font-medium text-yellow-600 dark:text-yellow-400 mt-1">{stats.kismiSevkMetre.toFixed(1)} m</p>
            </div>
            <div className="bg-yellow-100 dark:bg-yellow-800/30 p-2 sm:p-3 rounded-lg">
              <Package className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Arama */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Sipariş no, desen no, müşteri ara..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Durum Filtresi */}
          <div className="flex gap-2">
            <button
              onClick={() => handleDurumChange('')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                durumFilter === ''
                  ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border border-purple-300 dark:border-purple-700'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Tümü
            </button>
            <button
              onClick={() => handleDurumChange('Aktif')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                durumFilter === 'Aktif'
                  ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-700'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Aktif
            </button>
            <button
              onClick={() => handleDurumChange('Kısmi Sevk')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                durumFilter === 'Kısmi Sevk'
                  ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border border-yellow-300 dark:border-yellow-700'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Kısmi Sevk
            </button>
          </div>

          {/* Desen Onay Durumu Filtresi */}
          <div className="flex gap-2">
            <select
              value={onayDurumuFilter}
              onChange={(e) => setOnayDurumuFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Tümü</option>
              <option value="onayli">Onaylı</option>
              <option value="revize">Revize</option>
              <option value="onay_bekliyor">Onay Bekliyor</option>
            </select>
          </div>
        </div>
      </div>

      {/* Sipariş Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <RefreshCw className="h-12 w-12 text-gray-400 dark:text-gray-500 animate-spin" />
            <p className="mt-4 text-gray-500 dark:text-gray-400">Siparişler yükleniyor...</p>
          </div>
        ) : filteredSiparisler.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Package className="h-12 w-12 text-gray-400 dark:text-gray-500" />
            <p className="mt-2 text-gray-500 dark:text-gray-400">Henüz sipariş bulunmuyor</p>
          </div>
        ) : (
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {filteredSiparisler.map((item) => {
                const { siparis, varyant } = item;
                
                // Görsel URL'lerini al - Modal'daki mantıkla aynı
                let imageUrl = null;
                
                // Önce varyant.desen'den resim al
                if (varyant.desen?.ozel_gorsel_url) {
                  imageUrl = varyant.desen.ozel_gorsel_url.startsWith('http') 
                    ? varyant.desen.ozel_gorsel_url 
                    : `${config.backendUrl}${varyant.desen.ozel_gorsel_url.startsWith('/') ? varyant.desen.ozel_gorsel_url : `/${varyant.desen.ozel_gorsel_url}`}`;
                } else if (varyant.desen?.gorsel_urls) {
                  imageUrl = processImageUrl(varyant.desen.gorsel_urls);
                }
                
                // Varyant.desen'de resim yoksa, siparis.gorsel_urls'e bak (fallback)
                if (!imageUrl && siparis.gorsel_urls) {
                  imageUrl = processImageUrl(siparis.gorsel_urls);
                }

                return (
                  <div
                    key={`${siparis.siparis_no}-${varyant.id}`}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 bg-white dark:bg-gray-800"
                  >
                    {/* Görsel */}
                    <div
                      className="relative h-48 bg-gray-100 dark:bg-gray-700 rounded-t-lg overflow-hidden cursor-pointer"
                      onClick={() => handleShowDetail(item)}
                    >
                      {imageUrl ? (
                        <BaskiGorselComponent
                          imageUrl={imageUrl}
                          alt={varyant.desen?.desen_no || siparis.siparis_no || 'Varyant'}
                          useThumbnail
                          priority={false}
                        />
                      ) : null}
                      {/* Fallback for no image */}
                      <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500" style={{ display: imageUrl ? 'none' : 'flex' }}>
                        <div className="text-center">
                          <FileText className="h-16 w-16 mx-auto mb-2" />
                          <p className="text-sm">Resim Yok</p>
                        </div>
                      </div>

                      {/* YENİ/RPT Badge - Sol Üst */}
                      <div className="absolute top-2 left-2 z-20">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          varyant.is_first_order === true || varyant.is_first_order === undefined
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                            : 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300'
                        }`}>
                          {varyant.is_first_order === true || varyant.is_first_order === undefined ? 'YENİ' : 'RPT'}
                        </span>
                      </div>
                      
                      {/* Durum Badge - Sağ Üst */}
                      <div className="absolute top-2 right-2 z-20">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          varyant.durum === 'Aktif' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                          varyant.durum === 'Kısmi Sevk' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                          'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                        }`}>
                          {varyant.durum || 'Aktif'}
                        </span>
                      </div>
                      
                      {/* Desen Onay Durumu Badge - Sağ Alt */}
                      {varyant.desen?.onay_durumu && (
                        <div className="absolute bottom-2 right-2 z-20">
                          <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full shadow-lg ${
                            varyant.desen.onay_durumu === 'onay_bekliyor' 
                              ? 'bg-yellow-500/90 text-yellow-900 dark:bg-yellow-600/90 dark:text-yellow-100' :
                            varyant.desen.onay_durumu === 'revize' 
                              ? 'bg-red-500/90 text-red-900 dark:bg-red-600/90 dark:text-red-100' :
                            varyant.desen.onay_durumu === 'onayli' 
                              ? 'bg-green-500/90 text-green-900 dark:bg-green-600/90 dark:text-green-100' :
                              'bg-gray-500/90 text-gray-900 dark:bg-gray-600/90 dark:text-gray-100'
                          }`}>
                            {varyant.desen.onay_durumu === 'onay_bekliyor' ? 'Onay Bekliyor' :
                             varyant.desen.onay_durumu === 'revize' ? 'Revize' :
                             varyant.desen.onay_durumu === 'onayli' ? 'Onaylı' : ''}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Bilgiler */}
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-2.5 text-xs">
                        <div className="col-span-2 flex items-center justify-between">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {siparis.siparis_no}
                          </h4>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(siparis.siparis_tarihi).toLocaleDateString('tr-TR')}
                          </span>
                        </div>
                        <div className="col-span-2 flex items-center justify-between">
                          <p className="text-gray-500 dark:text-gray-400 truncate">
                            {varyant.desen?.desen_no || 'Varyant'}
                          </p>
                          <p className="text-gray-500 dark:text-gray-400 text-right text-xs ml-2 truncate">
                            {siparis.musteri_adi || 'Müşteri yok'}
                          </p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-500 dark:text-gray-400">
                            {(() => {
                              const toplam = parseFloat(varyant.metre || 0);
                              const kabul = parseFloat(varyant.kabul_edilen_metre || 0);
                              const kalan = Math.max(0, toplam - kabul);
                              const toplamInt = Math.round(toplam);
                              const kalanInt = Math.round(kalan);
                              if (varyant.durum === 'Kısmi Sevk') {
                                return `${toplamInt} m (Kalan ${kalanInt} m)`;
                              }
                              return `${toplamInt} m`;
                            })()}
                          </p>
                        </div>
                      </div>
                      
                      {/* Detayları Görüntüle Butonu */}
                      <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => handleShowDetail(item)}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                        >
                          <Eye className="h-4 w-4" />
                          <span>Detayları Görüntüle</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Sipariş Detay Modal */}
      {selectedSiparis && (
        <SiparisDetayModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedSiparis(null);
          }}
          siparis={selectedSiparis}
          isPublicPage={true}
        />
      )}
    </div>
  );
};

export default DigitalPrintingAktifSiparislerTab;
